/**
 * A menu that owns its trigger and positioning: finixt-ui's Dropdown look on
 * Radix DropdownMenu, which supplies what the package's own menu lacks for a
 * console used from the keyboard: Enter and Space open it, the arrow keys and
 * typeahead move through it, Escape closes it and returns focus to the
 * trigger, and the menu has its roles. The drawing is finixt-ui's Popover,
 * PopoverItem, PopoverSection, and PopoverDivider, token for token.
 *
 * The trigger must be a focusable element, normally a button; Radix renders
 * it as is and attaches the menu to it.
 */
import * as Menu from '@radix-ui/react-dropdown-menu';
import {
  cloneElement,
  isValidElement,
  useState,
  type CSSProperties,
  type FocusEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';

import { Icon } from './finixt.js';

const MENU_Z_INDEX = 86;

const PANEL_STYLE: CSSProperties = {
  zIndex: MENU_Z_INDEX,
  maxHeight: 'var(--radix-dropdown-menu-content-available-height)',
  overflowY: 'auto',
  background: 'var(--surface-card)',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius-panel)',
  boxShadow: 'var(--shadow-popover)',
  padding: 5,
  animation: 'popin var(--dur-fast) var(--ease-out)',
};

const ITEM_STYLE: CSSProperties = {
  display: 'flex',
  gap: 9,
  padding: '7px 9px',
  borderRadius: 'var(--radius-control)',
  fontSize: 'var(--text-body-sm)',
  textDecoration: 'none',
  userSelect: 'none',
};

export interface DropdownProps {
  /** A focusable element, normally a button. */
  trigger: ReactNode;
  /** Panel width in px. Default 232, as finixt-ui's. */
  width?: number | undefined;
  /** start aligns the panel to the trigger's left edge, end to its right. */
  align?: 'start' | 'end' | undefined;
  /** Flips automatically when there is no room. */
  side?: 'bottom' | 'top' | undefined;
  /** Gap between trigger and panel. Default 6. */
  offset?: number | undefined;
  /** Omit for self-managed open state. */
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  /** DropdownItem, DropdownSection, DropdownDivider. */
  children?: ReactNode;
}

/** The menu. */
/** Radix hands focus back to the trigger on close; the person sees the control inside it. */
function focusInner(event: FocusEvent<HTMLSpanElement>): void {
  if (event.target !== event.currentTarget) return;
  event.currentTarget.querySelector<HTMLElement>('button, a, [tabindex]')?.focus();
}

export function Dropdown({
  trigger,
  width = 232,
  align = 'start',
  side = 'bottom',
  offset = 6,
  open,
  onOpenChange,
  children,
}: DropdownProps) {
  const [selfOpen, setSelfOpen] = useState(false);
  const isOpen = open ?? selfOpen;
  const labelled = isValidElement<Record<string, unknown>>(trigger)
    ? cloneElement(trigger, { 'aria-haspopup': 'menu', 'aria-expanded': isOpen })
    : trigger;
  return (
    <Menu.Root
      modal={false}
      {...(open !== undefined ? { open } : {})}
      onOpenChange={(next) => {
        setSelfOpen(next);
        onOpenChange?.(next);
      }}
    >
      {/* finixt-ui's Button and IconButton do not forward refs, so Radix's
          anchor ref lands on this span, which wraps the control tightly;
          without it the panel opens at translate(0, -200%), off the page. */}
      <Menu.Trigger asChild>
        <span style={{ display: 'inline-flex' }} tabIndex={-1} onFocus={focusInner}>
          {labelled}
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          align={align}
          side={side}
          sideOffset={offset}
          collisionPadding={8}
          style={{ ...PANEL_STYLE, width }}
        >
          {children}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

export interface DropdownItemProps {
  icon?: string | undefined;
  label: string;
  /** Second line: what the item does when the label cannot say. */
  sub?: string | undefined;
  /** Mono shortcut hint, right-aligned. */
  kbd?: string | undefined;
  /** A boolean renders a checkmark column in place of the icon. */
  checked?: boolean | undefined;
  tone?: 'default' | 'danger' | undefined;
  disabled?: boolean | undefined;
  onSelect?: (() => void) | undefined;
  /** Renders the item as a router link so it has an href and opens in a new tab on middle click. */
  to?: string | undefined;
  /** A leading node in place of the icon: an avatar, a tile. */
  leading?: ReactNode;
  /** A trailing node after the label: a badge. */
  trailing?: ReactNode;
}

/** One row of the menu. */
export function DropdownItem({
  icon,
  label,
  sub,
  kbd,
  checked,
  tone = 'default',
  disabled = false,
  onSelect,
  to,
  leading,
  trailing,
}: DropdownItemProps) {
  const danger = tone === 'danger';
  const style: CSSProperties = {
    ...ITEM_STYLE,
    alignItems: sub ? 'flex-start' : 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.42 : 1,
    color: danger ? 'var(--danger)' : 'var(--grey-800)',
  };
  const body = (
    <>
      {checked !== undefined ? (
        <span style={{ width: 17, flexShrink: 0, display: 'flex', marginTop: sub ? 1 : 0 }}>
          {checked ? <Icon name="check" size={16} color="var(--accent)" /> : null}
        </span>
      ) : leading !== undefined ? (
        leading
      ) : icon ? (
        <Icon
          name={icon}
          size={17}
          color={danger ? 'var(--danger)' : 'var(--text-soft)'}
          style={{ flexShrink: 0, marginTop: sub ? 1 : 0 }}
        />
      ) : null}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        {sub ? (
          <span
            style={{
              display: 'block',
              fontSize: 'var(--text-meta)',
              color: 'var(--text-faint)',
              lineHeight: 'var(--leading-body)',
              marginTop: 2,
            }}
          >
            {sub}
          </span>
        ) : null}
      </span>
      {trailing}
      {kbd ? (
        <span
          className="mono"
          style={{ flexShrink: 0, fontSize: 'var(--text-label-lg)', color: 'var(--text-ghost)' }}
        >
          {kbd}
        </span>
      ) : null}
    </>
  );
  const shared = {
    className: 'ui-menu-item',
    'data-tone': tone,
    disabled,
    style,
    ...(onSelect ? { onSelect } : {}),
  };
  if (to !== undefined) {
    return (
      <Menu.Item asChild {...shared}>
        <Link to={to}>{body}</Link>
      </Menu.Item>
    );
  }
  return <Menu.Item {...shared}>{body}</Menu.Item>;
}

export interface DropdownCheckboxItemProps {
  label: string;
  /** Second line: what the column holds when the label cannot say. */
  sub?: string | undefined;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  disabled?: boolean | undefined;
}

/** A row that toggles and keeps the menu open, for a picker of several things. */
export function DropdownCheckboxItem({
  label,
  sub,
  checked,
  onCheckedChange,
  disabled = false,
}: DropdownCheckboxItemProps) {
  return (
    <Menu.CheckboxItem
      className="ui-menu-item"
      checked={checked}
      onCheckedChange={onCheckedChange}
      onSelect={(event) => event.preventDefault()}
      disabled={disabled}
      style={{
        ...ITEM_STYLE,
        alignItems: sub ? 'flex-start' : 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.42 : 1,
        color: 'var(--grey-800)',
      }}
    >
      <span style={{ width: 17, flexShrink: 0, display: 'flex', marginTop: sub ? 1 : 0 }}>
        <Menu.ItemIndicator>
          <Icon name="check" size={16} color="var(--accent)" />
        </Menu.ItemIndicator>
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block' }}>{label}</span>
        {sub ? (
          <span
            style={{
              display: 'block',
              fontSize: 'var(--text-meta)',
              color: 'var(--text-faint)',
              lineHeight: 'var(--leading-body)',
              marginTop: 2,
            }}
          >
            {sub}
          </span>
        ) : null}
      </span>
    </Menu.CheckboxItem>
  );
}

export interface DropdownSectionProps {
  label: string;
}

/** Mono uppercase group label inside the menu. */
export function DropdownSection({ label }: DropdownSectionProps) {
  return (
    <Menu.Label
      className="mono"
      style={{
        fontSize: 'var(--text-label-sm)',
        letterSpacing: 'var(--tracking-label-tight)',
        textTransform: 'uppercase',
        color: 'var(--text-faint)',
        padding: '7px 9px 5px',
      }}
    >
      {label}
    </Menu.Label>
  );
}

/** Hairline between groups of items. */
export function DropdownDivider() {
  return (
    <Menu.Separator style={{ height: 1, background: 'var(--border-divider)', margin: '5px 0' }} />
  );
}
